import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, type Theme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { giveFeedback, type FeedbackIn } from '../api/feedbackApi';
import { ReportProblemCard } from './ReportProblemCard';

type Props = { about: Omit<FeedbackIn, 'verdict' | 'reason' | 'note'> };

/** Helpful or wrong, with why. Research reviews the flags, and each one can become an accuracy-test case.
 * Placed in a wrapping actions row: the report card takes the full width, so it drops below the row. */
export function AnswerFeedback({ about }: Props) {
  const [reporting, setReporting] = useState(false);
  const send = useMutation({ mutationFn: giveFeedback });

  if (send.isSuccess) {
    return (
      <Typography variant="caption" role="status" sx={{ color: 'text.secondary', px: 0.5 }}>
        {send.data.verdict === 'down' ? 'Thanks. The research team will review this answer.' : 'Thanks for the feedback.'}
      </Typography>
    );
  }
  return (
    <>
      <Stack direction="row" sx={{ gap: 0.25 }}>
        <Tooltip title="Helpful">
          <IconButton size="small" aria-label="Helpful" sx={thumbSx} disabled={send.isPending} onClick={() => { send.mutate({ ...about, verdict: 'up' }); }}>
            <ThumbsUp size={15} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Report a problem">
          <IconButton size="small" aria-label="Report a problem" aria-expanded={reporting} sx={reporting ? activeThumbSx : thumbSx} onClick={() => { setReporting((open) => !open); }}>
            <ThumbsDown size={15} />
          </IconButton>
        </Tooltip>
      </Stack>
      {reporting ? (
        <ReportProblemCard
          sending={send.isPending}
          failed={send.isError}
          onSend={(reason, note) => { send.mutate({ ...about, verdict: 'down', reason, note }); }}
          onClose={() => { setReporting(false); }}
        />
      ) : null}
    </>
  );
}

const thumbSx = { color: 'text.secondary', '&:hover': { color: 'text.primary' } } as const;

const activeThumbSx = (theme: Theme) => ({
  color: theme.palette.error.main,
  bgcolor: alpha(theme.palette.error.main, 0.1),
  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) },
});
